import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { personalInfo } from '../../data/mock';
import Sparkle from '../Sparkle';
import soundManager from '../../utils/soundEffects';

// EmailJS Configuration
// Sign up at https://www.emailjs.com/
// Then replace these with your actual keys:
const EMAILJS_SERVICE_ID = 'YOUR_SERVICE_ID'; // e.g., 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID'; // e.g., 'template_xyz789'
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY'; // e.g., 'user_ABC123XYZ'

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    // Check if EmailJS is configured
    if (EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID' || 
        EMAILJS_TEMPLATE_ID === 'YOUR_TEMPLATE_ID' || 
        EMAILJS_PUBLIC_KEY === 'YOUR_PUBLIC_KEY') {
      setError('⚠️ EmailJS not configured yet! Please add your API keys.');
      setSending(false);
      soundManager.playClick();
      return;
    }

    try {
      // Send email using EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_email: personalInfo.email // Your email where messages will be sent
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      soundManager.playSuccess();
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      
      setTimeout(() => {
        setSubmitted(false);
      }, 4000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setError('❌ Failed to send message. Please try again!');
      soundManager.playClick();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="window mb-6">
      <div className="window-header">
        <span>✉️ Contact Me ✉️</span>
      </div>
      <div className="window-body">
        <div className="text-center mb-4">
          <div className="text-6xl mb-2 animate-bounce">💌</div>
          <p className="text-lg font-bold pixel-text" style={{ color: '#ff1493' }}>
            Let's Connect! 💖
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Contact Info */}
          <div className="space-y-3">
            <div className="bg-pink-100 p-4 border-2 border-pink-400">
              <p className="font-bold text-sm mb-2 flex items-center gap-2">
                <span>📧</span> Email:
              </p>
              <Sparkle onInteract={() => soundManager.playClick()}>
                <a
                  href={`mailto:${personalInfo.email}`}
                  onClick={() => soundManager.playClick()}
                  className="text-sm break-all hover:text-purple-600"
                >
                  {personalInfo.email}
                </a>
              </Sparkle>
            </div>

            <div className="bg-blue-100 p-4 border-2 border-blue-400">
              <p className="font-bold text-sm mb-2 flex items-center gap-2">
                <span>💼</span> LinkedIn:
              </p>
              <Sparkle onInteract={() => soundManager.playClick()}>
                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => soundManager.playClick()}
                  className="text-sm break-all hover:text-purple-600"
                >
                  Connect with me!
                </a>
              </Sparkle>
            </div>

            <div className="bg-purple-100 p-4 border-2 border-purple-400 text-center">
              <p className="text-xs mb-2 flex items-center justify-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                Status: Online
              </p>
              <div className="text-4xl">🧙‍♀️✨</div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-4 border-4 border-double border-gray-400">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-3">
                {error && (
                  <div className="bg-red-100 border-2 border-red-400 p-2 text-xs text-red-700">
                    {error}
                  </div>
                )}
                
                <div>
                  <label className="block text-xs font-bold mb-1">Name:</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onClick={() => soundManager.playClick()}
                    required
                    disabled={sending}
                    className="w-full px-2 py-1 border-2 border-gray-400 text-sm focus:outline-none focus:border-pink-400 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold mb-1">Email:</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onClick={() => soundManager.playClick()}
                    required
                    disabled={sending}
                    className="w-full px-2 py-1 border-2 border-gray-400 text-sm focus:outline-none focus:border-pink-400 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold mb-1">Message:</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onClick={() => soundManager.playClick()}
                    required
                    disabled={sending}
                    rows="4"
                    className="w-full px-2 py-1 border-2 border-gray-400 text-sm focus:outline-none focus:border-pink-400 resize-none disabled:bg-gray-100"
                  ></textarea>
                </div>

                <Sparkle onInteract={() => soundManager.playClick()}>
                  <button
                    type="submit"
                    disabled={sending}
                    className="retro-btn w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sending ? '📤 Sending...' : '📨 Send Message!'}
                  </button>
                </Sparkle>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-2 animate-bounce">✨</div>
                <p className="font-bold pixel-text" style={{ color: '#ff1493' }}>
                  Message Sent! 🎉
                </p>
                <p className="text-xs mt-2">Thanks for reaching out!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
